import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { AssociationByCandidateIdJobIdObj } from '@collections/associations';

import { isCurrentUserHiringManager } from '@collections/users/users.selectors';
import { Store } from '@ngrx/store';
import { parseDate } from '@shared';
import { AtsButtonType } from '@shared/ats-button/ats-button.type';
import { CandidateAdapter } from '@shared/models';
import { IsMobileViewService } from '@shared/services/is-mobile-view.service';
import { ReferralService } from '@shared/services/referral.service';
import { AvatarBackgroundSizes, AvatarShapes } from 'mojo-avatar';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CandidateCardSelectEvent } from '../../models/candidate-card-select-event';
import { candidatesConfig } from '@modules/candidates/candidates.config';
import { SideNavService } from '@shared/candidate/side-nav-candidate/services/side-nav.service';
import { sortByParamConfig } from '@shared/models/sortby-param';
import { MatDialog } from '@angular/material/dialog';
import { CandidatePdfPreviewComponent } from '@shared/pdf-preview-tooltip/candidate-pdf-preview/candidate-pdf-preview.component';

@Component({
  selector: 'mojo-candidate-card',
  templateUrl: './candidate-card.component.html',
  styleUrls: ['./candidate-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateCardComponent implements OnInit, OnDestroy {
  @Input() candidate: CandidateAdapter;
  @Input() showAvatarLabel = false;
  @Input() showLabels = true;
  @Input() showContactInfo = true;
  @Input() selectable = false;
  @Input() inModal = false;
  /** Center content vertically */
  @Input() centerContent = false;
  @Input() enableSelection = false;
  @Input() jobId?: number;
  @Input() candidateContact = true;
  @Input() disableNavigation = false;
  @Input() hideResumeDownloadButton = false;
  @Input() displayCandidateCardArea = true;
  @Input() addToTalentPool = false;
  @Input() hasStatus = false;
  @Input() talentPoolSideDrawerSelectedId;
  @Output() avatarClicked = new EventEmitter();
  @Output() select = new EventEmitter<CandidateCardSelectEvent>();
  @Input() sideDrawerCandidatesList: CandidateAdapter[];
  @Input() fromDashboardLanding = false;
  @Input() fromTalentPool = false;
  @Input() showQA = false;
  @Input() sortDate: string;
  @Input() sortLabel: string;
  candidateViewPrivilege = candidatesConfig.privileges.candidates.view;

  @ViewChild('card', { static: false, read: ElementRef }) card: ElementRef;

  candidateAtsButtonType = AtsButtonType.CANDIDATE;

  sidebarOpened = false;
  avatarPhotoShape = AvatarShapes.circle;
  avatarBackground = AvatarBackgroundSizes.cover;
  isNotHiringManager$: Observable<boolean>;
  transformX = 0;
  associationDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
  subs = new Subscription();

  referralDate: number;
  sideDrawerCandidateId$ = this.sideNavService.candidateId$;

  constructor(
    private store: Store<{}>,
    private renderer: Renderer2,
    public isMobileViewService: IsMobileViewService,
    private referralService: ReferralService,
    public sideNavService: SideNavService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.isNotHiringManager$ = this.store
      .select(isCurrentUserHiringManager)
      .pipe(map((isHM) => !isHM));
  }

  ngOnInit(): void {
    if (!!this.jobId) {
      this.subs.add(
        this.store
          .select(
            AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(
              this.candidate.id,
              this.jobId,
            ),
          )
          .subscribe((association) => {
            if (!!association?.appliedAt) {
              this.associationDate$.next(parseDate(association?.appliedAt));
              this.cdr.detectChanges();
            }
          }),
      );
    }

    if (!!this.candidate.referredId) {
      this.subs.add(
        this.referralService
          .get({ id: this.candidate.referredId })
          .pipe(
            take(1),
            map((r) => r.createdDate),
          )
          .subscribe((date) => {
            this.referralDate = date;
            this.cdr.detectChanges();
          }),
      );
    }
  }

  getMobileAddress(candidate: CandidateAdapter) {
    let shortAddress = candidate.city && `${candidate.city}, `;
    shortAddress += candidate.country && `${candidate.country}`;
    return shortAddress;
  }

  selectCandidateCard(candidateId: number) {
    this.select.emit({ candidateId });
  }

  private setTransformValueOnCard() {
    this.renderer.setStyle(
      this.card.nativeElement,
      'transform',
      `translateX(${this.transformX}px)`,
    );
  }

  getDate() {
    if (this.shouldHideDateWhenSorting()) {
      return '';
    }
    if (!!this.sortDate) {
      return this.sortDate;
    }
    if (!!this.associationDate$.getValue()) {
      return this.associationDate$.getValue();
    }
    if (!!this.referralDate) {
      return this.referralDate;
    }
    return this.candidate.createdDate;
  }

  getDateLabel() {
    if (this.shouldHideDateWhenSorting()) {
      return '';
    }
    if (!!this.sortLabel) {
      return this.sortLabel;
    }
    if (!!this.associationDate$.getValue()) {
      return 'Application Date: ';
    }
    if (!!this.referralDate) {
      return 'Referral Date: ';
    }
    return 'Created: ';
  }

  private shouldHideDateWhenSorting() {
    return (
      !this.sortDate &&
      !!this.sortLabel &&
      this.sortLabel !== sortByParamConfig.applicationDate.label
    );
  }

  sideNavAction() {
    this.sidebarOpened = !this.sidebarOpened;
    this.isNotHiringManager$.pipe(take(1)).subscribe((isNotHM) => {
      if (!isNotHM && this.candidateContact && !this.hideResumeDownloadButton) {
        this.dialog.open(CandidatePdfPreviewComponent, {
          data: { candidate: this.candidate },
          panelClass: 'candidate-pdf-preview',
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
